import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  isAnyOf,
} from '@reduxjs/toolkit'
import { RootState } from './'
import { UserInfo, Login, NewsletterSubscriptionRequest } from '../types'
import okaoAxios, { okaoAxiosNoAuth } from '../helpers/axios'
import { IRestaurantLocation, Location } from '../helpers/location-helper'
import { AlertColor } from '@mui/material/Alert'
import { safeLocalStorage, setAccessTokenToLocal } from '../helpers/auth-helper'
import {
  Restaurant,
  RestaurantOnboardRequest,
  RestaurantUpdateContactRequest,
} from '../types/restaurantTypes'
import { infoLog } from '../utils/log-helper'
import {
  addVersionToLoginPayload,
  migrateLoginPayload,
} from '../helpers/loginStorageMigration'

export interface ISecureSlice {
  accessToken: string | undefined
  user: UserInfo | null
  login: Login
  location?: Location | undefined
  shouldOpen: boolean
  message: string | undefined
  severity: AlertColor | undefined
  imageUrl: string
  retry: number
  subscribeSuccess: boolean
  status: string
  restaurantLocation: IRestaurantLocation | null
}

const initialState: ISecureSlice = {
  accessToken: undefined,
  user: null,
  login: {},
  shouldOpen: false,
  message: undefined,
  severity: undefined,
  imageUrl: '',
  retry: 0,
  subscribeSuccess: false,
  status: 'idle',
  restaurantLocation: null,
}

export const getUserInfo = createAsyncThunk('api/getUserInfo', async () => {
  const response = await okaoAxios.get(
    `https://${process.env.REACT_APP_AUTH0_DOMAIN ?? ''}/userinfo`,
  )
  return response?.data
})

export interface LoginAuditFlags {
  menuScanFlow?: boolean
  saveFlow?: boolean
  groupSessionFlow?: boolean
  pointsClaimFlow?: boolean
  referralFlow?: boolean
}

export const loginAudit = createAsyncThunk(
  'api/loginAudit',
  async (flags: LoginAuditFlags = {}) => {
    const {
      menuScanFlow = false,
      saveFlow = false,
      groupSessionFlow = false,
      pointsClaimFlow = false,
      referralFlow = false,
    } = flags
    // Mirror the menuScanFlow signal: the backend keys the acquisition channel (and whether to
    // skip onboarding) off these headers. saveFlow marks the anon-save -> signup -> resume flow;
    // groupSessionFlow / pointsClaimFlow mark the group-dining-claim and anon-points-claim
    // signup levers; referralFlow marks a referral-link signup — all skip onboarding server-side
    // (persisted, via AcquisitionChannel), not just once client-side.
    const headers: Record<string, string> = {}
    if (menuScanFlow) headers['X-Menu-Scan-Flow'] = 'true'
    if (saveFlow) headers['X-Save-Flow'] = 'true'
    if (groupSessionFlow) headers['X-Group-Session-Flow'] = 'true'
    if (pointsClaimFlow) headers['X-Points-Claim-Flow'] = 'true'
    if (referralFlow) headers['X-Referral-Flow'] = 'true'
    const response = await okaoAxios.post('/login-lite', null, { headers })
    return response?.data
  },
)

export const updateRestaurantOnboard = createAsyncThunk(
  'api/updateRestaurantOnboard',
  async (nmr: RestaurantUpdateContactRequest) => {
    const response = await okaoAxios.post('/restaurants/onboard-update', nmr)
    return response?.data
  },
)

export const restaurantOnboardRequest = createAsyncThunk(
  'api/restaurantOnboard',
  async (nmr: RestaurantOnboardRequest) => {
    const response = await okaoAxios.post('/restaurants/onboard', nmr)
    return response?.data
  },
)

export const subscribeToNewsletter = createAsyncThunk(
  'api/subscribeNewsletter',
  async (nsr: NewsletterSubscriptionRequest) => {
    const response = await okaoAxiosNoAuth().post('/user-subscription', nsr)
    return response?.data
  },
)

export const deleteUserProfile = createAsyncThunk(
  'api/delete-account',
  async (params: any) => {
    const response = await okaoAxios.delete('/eater/profile/delete-account', {
      data: params,
    })
    return response
  },
)

export const recoverUserAccount = createAsyncThunk(
  'api/recoverUserAccount',
  async () => {
    const response = await okaoAxios.put('/eater/profile/recover-account')
    return response
  },
)

export const recoverUserBlockedAccount = createAsyncThunk(
  'api/recoverUserBlockedAccount',
  async () => {
    const response = await okaoAxios.post('/eater/account-unblock-request')
    return response
  },
)

/**
 * Fetches lite session data from GET /login/session
 * Returns minimal eaterDTO fields (eaterId, step, subscribed, imageUrl, status)
 * Heavy collections (reviews, restaurantReviews, predictedSavedRestaurants) are NOT included
 * Should be called after loginAudit completes for routing decisions
 */
export const fetchSession = createAsyncThunk('api/fetchSession', async () => {
  const response = await okaoAxios.get('/login/session')
  return response?.data
})

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAccessToken: (state, action: PayloadAction<string | undefined>) => {
      state.accessToken = action.payload
      setAccessTokenToLocal(state.accessToken ?? '')
    },
    setLocation: (state, action: PayloadAction<Location | undefined>) => {
      state.location = action.payload
      safeLocalStorage.setItem('location', JSON.stringify(state.location))
    },
    setRestaurantLocation: (
      state,
      action: PayloadAction<IRestaurantLocation | null>,
    ) => {
      state.restaurantLocation = action.payload
    },
    setRestaurant: (state, action: PayloadAction<Restaurant>) => {
      state.login.restaurantDTO = action.payload
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload
    },
    setSubscribeSuccess: (state, action: PayloadAction<boolean>) => {
      state.subscribeSuccess = action.payload
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload
    },
    setSeverity: (state, action: PayloadAction<AlertColor>) => {
      state.severity = action.payload
    },
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.user = action?.payload
        safeLocalStorage.setItem('userInfo', JSON.stringify(state.user))
      })
      .addCase(loginAudit.fulfilled, (state, action) => {
        // Store lite payload with version
        const versionedPayload = addVersionToLoginPayload(action.payload)
        state.login = versionedPayload
        infoLog(action.payload)
        if (action.payload.eaterDTO != null) {
          state.imageUrl = action.payload.eaterDTO.imageUrl ?? '/images/male.png'
        } else {
          state.imageUrl = '/images/male.png'
        }
        safeLocalStorage.setItem('login', JSON.stringify(versionedPayload))
      })
      .addCase(loginAudit.rejected, (state, action) => {
        state.shouldOpen = true
        state.message =
          'Oops! We had a little trouble logging you into Resactly. Could you please try refreshing the page? Thanks for your understanding!'
        state.severity = 'error'
        location.reload()
      })
      .addCase(updateRestaurantOnboard.fulfilled, (state, action) => {
        const versionedPayload = addVersionToLoginPayload(action.payload)
        state.login = versionedPayload
        safeLocalStorage.setItem('login', JSON.stringify(versionedPayload))
      })
      .addCase(restaurantOnboardRequest.fulfilled, (state, action) => {
        const versionedPayload = addVersionToLoginPayload(action.payload)
        state.login = versionedPayload
        safeLocalStorage.setItem('login', JSON.stringify(versionedPayload))
      })

      .addCase(subscribeToNewsletter.fulfilled, (state, action) => {
        state.subscribeSuccess = true
        state.status = 'idle'
      })
      .addCase(subscribeToNewsletter.rejected, (state, action) => {
        state.message =
          'Oops! Please log in first, then subscribe. Thanks for your understanding!'
        state.severity = 'error'
        state.status = 'error'
        state.shouldOpen = true
      })
      .addCase(deleteUserProfile.fulfilled, (state, action) => {
        state.severity = 'success'
        safeLocalStorage.clear()
        state.status = 'idle'
      })
      .addCase(deleteUserProfile.rejected, (state, action) => {
        state.message =
          'Oops! We had a little trouble deleting your account. Could you please try refreshing the page? Thanks for your understanding!'
        state.severity = 'error'
        state.status = 'error'
        state.shouldOpen = true
      })
      .addCase(recoverUserAccount.fulfilled, (state, action) => {
        const versionedPayload = addVersionToLoginPayload(action.payload.data)
        state.login = versionedPayload
        safeLocalStorage.setItem('login', JSON.stringify(versionedPayload))
        state.status = 'idle'
      })
      .addCase(recoverUserAccount.rejected, (state, action) => {
        state.shouldOpen = true
        state.message =
          'Oops! We had a little trouble recovering your account. Could you please try refreshing the page? Thanks for your understanding!'
        state.severity = 'error'
        state.status = 'error'
        state.shouldOpen = true
      })
      .addCase(recoverUserBlockedAccount.fulfilled, (state, action) => {
        state.message = action.payload.data.message
        state.shouldOpen = true
        state.status = 'idle'
      })
      .addCase(recoverUserBlockedAccount.rejected, (state, action) => {
        state.shouldOpen = true
        state.message =
          'Oops! We had a little trouble while sending unblock request. Could you please try refreshing the page? Thanks for your understanding!'
        state.severity = 'error'
        state.status = 'error'
      })
      .addMatcher(
        isAnyOf(
          recoverUserAccount.pending,
          recoverUserBlockedAccount.pending,
          deleteUserProfile.pending,
          subscribeToNewsletter.pending,
        ),
        (state, action) => {
          state.status = 'loading'
        },
      )
  },
})

export const selectAccessToken = (state: RootState): string | undefined =>
  state.auth.accessToken ?? safeLocalStorage.getItem('access-token') ?? ''
export const selectUserInfo = (state: RootState): UserInfo | null =>
  state.auth.user
export const selectLogin = (state: RootState): Login | undefined => {
  // If state has login, use it (already migrated)
  if (state.auth.login && Object.keys(state.auth.login).length > 0) {
    return state.auth.login
  }
  // Otherwise, migrate from localStorage
  return migrateLoginPayload(safeLocalStorage.getItem('login'))
}
export const selectLocation = (state: RootState): Location | undefined =>
  state.auth.location
export const selectRestaurant = (state: RootState): Restaurant | undefined =>
  state.auth.login?.restaurantDTO as Restaurant | undefined
export const selectRestaurantAlias = (state: RootState): string | undefined =>
  state.auth.login?.restaurantDTO?.alias ?? ''
export const selectShouldOpen = (state: RootState): boolean =>
  state.auth.shouldOpen
export const selectMessage = (state: RootState): string | undefined =>
  state.auth.message
export const selectSeverity = (state: RootState): AlertColor | undefined =>
  state.auth.severity
export const selectImageUrl = (state: RootState): string => state.auth.imageUrl
export const selectSubscribeSuccess = (state: RootState): boolean =>
  state.auth.subscribeSuccess
export const selectIsLoading = (state: RootState): boolean =>
  state.auth.status === 'loading'
export const selectRestaurantLocation = (
  state: RootState,
): IRestaurantLocation | null => state.auth.restaurantLocation
export const selectRestaurantIsSubscribed = (state: RootState): boolean => {
  const login = selectLogin(state)
  // ACTIVE status means the restaurant has an active subscription
  return login?.status === 'ACTIVE'
}
export const selectEaterIsSubscribed = (state: RootState): boolean => {
  const login = selectLogin(state)
  // Check the backend subscribed field from eaterDTO
  if (login?.eaterDTO?.subscribed !== undefined) {
    return login.eaterDTO.subscribed
  }
  // Fallback to status check for backward compatibility
  return login?.status === 'SUBSCRIBED'
}

export const {
  setAccessToken,
  setLocation,
  setRestaurant,
  setOpen,
  setMessage,
  setSeverity,
  setSubscribeSuccess,
  setRestaurantLocation,
  clearData,
} = authSlice.actions
export default authSlice.reducer
